import { MobXProviderContext } from "mobx-react";
import React, { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CERTIFICATIONS, DAYS, getErrorTitle, SKILLS } from "../../../constants/constants";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Divider } from "primereact/divider";
import { Checkbox } from "primereact/checkbox";
import { DropDownOption } from "../../../models/primereact";
import { HomeHiringJobProfile } from "../../../models/businessHomeProfile";
import { Calendar } from "primereact/calendar";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appFormSectionStyle,
  appFullDialogStyle,
  appMutedTextStyle,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

interface CaregiverHiringProfileModalProps {
  caregiverHiringProfileData: HomeHiringJobProfile | undefined;
  homeId: string | undefined;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
}

const CaregiverHiringProfileModal: FC<CaregiverHiringProfileModalProps> = ({
  isOpen,
  toggleModal,
  caregiverHiringProfileData,
  homeId,
  flag,
  setFlag
}) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const defaultValues = {
    homeId: homeId || "",
    lookingToHire: false,
    jobTitle: "",
    jobDescription: "",
    skills: [] as DropDownOption[],
    certifications: [] as DropDownOption[],
    expectedSalaryRange: "",
    relocationWillingness: false,
    livingArrangements: false,
    joiningDate: new Date(),
    availableDayShifts: [] as DropDownOption[],
    availableNightShifts: [] as DropDownOption[],
  }

  const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

  useEffect(() => {
    const fetchJobProfileDetails = async () => {
      if (!caregiverHiringProfileData) return;
      setLoading(true);
      try {
        reset({
          homeId: caregiverHiringProfileData.homeId,
          lookingToHire: caregiverHiringProfileData.lookingToHire,
          jobTitle: caregiverHiringProfileData.jobTitle,
          jobDescription: caregiverHiringProfileData.jobDescription,
          expectedSalaryRange: caregiverHiringProfileData.expectedSalaryRange,
          relocationWillingness: caregiverHiringProfileData.relocationWillingness,
          livingArrangements: caregiverHiringProfileData.livingArrangements,
          joiningDate: new Date(caregiverHiringProfileData.joiningDate!),
          skills: caregiverHiringProfileData.skills,
          certifications: caregiverHiringProfileData.certifications,
          availableDayShifts: caregiverHiringProfileData.availableDayShifts,
          availableNightShifts: caregiverHiringProfileData.availableNightShifts,
        });
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setLoading(false);
      }
    };

    fetchJobProfileDetails();
  }, [caregiverHiringProfileData]);

  const onSubmit = async (data: any) => {
    const tempData = {
      ...data,
      homeId: caregiverHiringProfileData ? caregiverHiringProfileData.homeId : homeId,
    };

    setErrorMessage(null);
    setLoading(true);
    try {
      await backendAPIStore.init();
      if (!caregiverHiringProfileData) {
        await backendAPIStore.addHomeHiringJobProfile(tempData);
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Job Profile created successfully!', life: 3000 });
      } else {
        await backendAPIStore.updateHomeHiringJobProfile(tempData);
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Job Profile updated successfully!', life: 3000 });
      }
    } catch (error: any) {
      setErrorMessage(error.message);
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: 'Failed to update', life: 3000 });
    } finally {
      reset();
      toggleModal(false);
      setLoading(false);
      setFlag(!flag);
    }
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const footerContent = renderDialogFooter({
    errorMessage,
    loading,
    onCancel: () => {
      toggleModal(false);
      reset();
    },
    onSubmit: handleSubmit(onSubmit),
    submitLabel: "Save Profile",
  });

  return (
    <Dialog
      header={renderSurfaceHeader("Employee Hiring Profile", "Define the role, qualifications, schedule, and compensation for this opening.", "pi pi-briefcase")}
      visible={isOpen}
      modal
      footer={footerContent}
      style={appFullDialogStyle}
      headerStyle={appDialogHeaderStyle}
      contentStyle={appDialogContentStyle}
      closeOnEscape={false}
      onHide={() => { if (!isOpen) return; toggleModal(false); reset(); }}>
      <Toast ref={toast} />
      <form>
        <div style={appAccentPanelStyle} className="mb-3">
          <div style={{ fontWeight: 700, color: "#9a3412" }}>Hiring overview</div>
          <div className="mt-2" style={appMutedTextStyle}>
            Complete the posting so caregivers can quickly understand requirements, shifts, and compensation.
          </div>
        </div>
        <div style={appFormSectionStyle}>
        <div className="field">
          <span className="">
            <label htmlFor="lookingToHire" className={classNames({ 'p-error': errors.lookingToHire })}>Are you currently looking to hire?</label>
            &nbsp;
            <Controller name="lookingToHire" control={control} render={({ field, fieldState }) => (
              <Checkbox id={field.name} checked={field.value} onChange={(e) => field.onChange(e.checked)}
                className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
          </span> &nbsp;
          <br />
          <small className="p-text-secondary">Note: Please uncheck this box if you are not currently looking to hire or if the position has been filled.</small>
        </div>
        <Divider align="center">
          <i className="pi pi-briefcase mr-2"></i>
          <strong>Basic Details</strong>
        </Divider>
        <div className="field">
          <span className="p-float-label">
            <Controller name="jobTitle" control={control} rules={{
              required: 'Job Title is required.',
              maxLength: { value: 100, message: 'Job Title must be at most 100 characters long.' },
            }} render={({ field, fieldState }) => (
              <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="jobTitle" className={classNames({ 'p-error': errors.jobTitle })}>Job Title</label>
          </span>
          {getFormErrorMessage('jobTitle')}
        </div>
        <div className="field">
          <span className="p-float-label">
            <Controller name="jobDescription" control={control} rules={{
              required: 'Job Description is required.',
              maxLength: { value: 1000, message: 'Job Description must be at most 1000 characters long.' },
            }} render={({ field, fieldState }) => (
              <InputTextarea rows={5} style={{ width: "100%" }} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="jobDescription" className={classNames({ 'p-error': errors.jobDescription })}>Job Description</label>
          </span>
          {getFormErrorMessage('jobDescription')}
        </div>

        <Divider align="center">
          <i className="pi pi-graduation-cap mr-2"></i>
          <strong>Qualifications</strong>
        </Divider>

        <div className="field">
          <span className="p-float-label">
            <Controller name="skills" control={control} rules={{
              required: 'At least one skill is required.',
            }} render={({ field, fieldState }) => (
              <MultiSelect id={field.name} value={field.value} optionLabel="name" style={{ width: '100%' }} multiple
                placeholder="Select Skills" display="chip" title="Select Skills" selectAll={true} selectAllLabel="Select All"
                onChange={(e) => field.onChange(e.value)} options={SKILLS} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="skills" className={classNames({ 'p-error': errors.skills })}>Skills</label>
          </span>
          {getFormErrorMessage('skills')}
        </div>
        <div className="field">
          <span className="p-float-label">
            <Controller name="certifications" control={control} rules={{
              required: 'At least one certification is required.',
            }} render={({ field, fieldState }) => (
              <MultiSelect id={field.name} value={field.value} optionLabel="name" style={{ width: '100%' }} multiple
                placeholder="Select Certifications" display="chip" title="Select Certifications" selectAll={true} selectAllLabel="Select All"
                onChange={(e) => field.onChange(e.value)} options={CERTIFICATIONS} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="certifications" className={classNames({ 'p-error': errors.certifications })}>Certifications</label>
          </span>
          {getFormErrorMessage('certifications')}
        </div>
        </div>

        <Divider align="center">
          <i className="pi pi-dollar mr-2"></i>
          <strong>Compensation & Availability</strong>
        </Divider>
        <div style={appFormSectionStyle}>
        <div className="field">
          <span className="p-float-label">
            <Controller name="expectedSalaryRange" control={control} rules={{
              required: 'Expected Salary Range is required.',
              min: { value: 10, message: 'Expected Salary Range must be at least $10/hr.' },
              max: { value: 100, message: 'Expected Salary Range must be at most $100/hr.' },
            }} render={({ field, fieldState }) => (
              <InputText type="number" id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="e.g., [18-25]" />
            )} />
            <label htmlFor="expectedSalaryRange" className={classNames({ 'p-error': errors.expectedSalaryRange })}>Expected Salary Range ($/hr)</label>
          </span>
          {getFormErrorMessage('expectedSalaryRange')}
        </div>

        <div className="field">
          <span className="">
            <label htmlFor="relocationWillingness" className={classNames({ 'p-error': errors.relocationWillingness })}>
              Do you want to relocate caregiver closer to your home?
            </label>
            &nbsp;
            <Controller name="relocationWillingness" control={control} render={({ field, fieldState }) => (
              <Checkbox id={field.name} checked={field.value} onChange={(e) => field.onChange(e.checked)}
                className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
          </span>
        </div>

        <div className="field">
          <span className="">
            <label htmlFor="livingArrangements" className={classNames({ 'p-error': errors.livingArrangements })}>
              Do you provide living arrangements for the caregiver?
            </label>
            &nbsp;
            <Controller name="livingArrangements" control={control} render={({ field, fieldState }) => (
              <Checkbox id={field.name} checked={field.value} onChange={(e) => field.onChange(e.checked)}
                className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
          </span>
        </div>

        <div className="field">
          <span className="p-float-label">
            <Controller name="joiningDate" control={control} rules={{
              required: 'Joining Date is required.',
            }} render={({ field, fieldState }) => (
              <Calendar id={field.name} value={field.value} onChange={(e) => field.onChange(e.value)}
                minDate={new Date()}
                className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="joiningDate" className={classNames({ 'p-error': errors.joiningDate })}>Expected Joining Date</label>
          </span>
          {getFormErrorMessage('joiningDate')}
        </div>

        <div className="field">
          <span className="p-float-label">
            <Controller name="availableDayShifts" control={control} render={({ field, fieldState }) => (
              <MultiSelect id={field.name} value={field.value} optionLabel="name" style={{ width: '100%' }} multiple
                placeholder="Select Available Day Shifts" display="chip" title="Select Available Day Shifts" selectAll={true} selectAllLabel="Select All"
                onChange={(e) => field.onChange(e.value)} options={DAYS} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="availableDayShifts" className={classNames({ 'p-error': errors.availableDayShifts })}>Required Day Shifts</label>
          </span>
          {getFormErrorMessage('availableDayShifts')}
        </div>

        <div className="field">
          <span className="p-float-label">
            <Controller name="availableNightShifts" control={control} render={({ field, fieldState }) => (
              <MultiSelect id={field.name} value={field.value} optionLabel="name" style={{ width: '100%' }} multiple
                placeholder="Select Available Night Shifts" display="chip" title="Select Available Night Shifts" selectAll={true} selectAllLabel="Select All"
                onChange={(e) => field.onChange(e.value)} options={DAYS} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="availableNightShifts" className={classNames({ 'p-error': errors.availableNightShifts })}>Required Night Shifts</label>
          </span>
          {getFormErrorMessage('availableNightShifts')}
        </div>
        </div>
      </form>
    </Dialog>
  );
};

export default CaregiverHiringProfileModal;
